/**
 * A mission as small steps. The bank row carries them (`steps`) when it has
 * been authored that way; an older row is split into its sentences, and a
 * row that is one sentence gets the three moves every mission has: pick the
 * person, do it, notice what happened. Nothing here is ever empty, so the
 * steps screen always has something to tick.
 */
import type { Mission } from "@/features/missionkit/api/use-mission";
import type { StepperGraph } from "../types";

/** The most steps a mission is cut into: past this they stop feeling small. */
const MAX_STEPS = 6;

const sentencesOf = (text: string) =>
  (text.match(/[^.?!]+[.?!]+[”’"']*\s*/g) ?? [text])
    .map((s) => s.trim())
    .filter(Boolean);

const capital = (s: string) => s.replace(/^./, (c) => c.toUpperCase());

export function stepsFor(
  mission: Pick<Mission, "mission" | "steps" | "helpText">,
): string[] {
  const authored = (mission.steps ?? []).map((s) => s.trim()).filter(Boolean);
  if (authored.length) return authored.slice(0, MAX_STEPS);
  const text = mission.mission.replace(/^your mission:\s*/i, "").trim();
  const sentences = sentencesOf(text);
  if (sentences.length >= 2)
    return sentences.slice(0, MAX_STEPS).map((s) => capital(s));
  return [
    "Pick the person. Say their name to yourself.",
    capital(text),
    "Notice what happened. You will write one line about it.",
  ];
}

/** What the handler says as each step lands; the last one asks for the stamp. */
export const STEP_CHEERS = [
  "Good. Keep going.",
  "That's the hard part done.",
  "The Enemy felt that one.",
  "Nice work, operative.",
  "One more and it's yours.",
];
export const ALL_DONE_CHEER = "Every step done. Stamp it.";

export const cheerFor = (doneCount: number, total: number) =>
  doneCount >= total
    ? ALL_DONE_CHEER
    : STEP_CHEERS[Math.min(doneCount - 1, STEP_CHEERS.length - 1)];

/** A mission's progress is keyed by the mission, never the step: the steps
 *  are the mission's, so coming back tomorrow finds them as they were left. */
export const progressKey = (missionId: number | string) =>
  `mission:${missionId}`;

/** The hub's branch for a mission: `M13` → `M13 S1`. */
export const missionEvent = (missionId: number | string) => `M${missionId}`;

/** A mission's chain of steps in the adventure (`WebSmMissionStep` with its
 *  MissionID), in StepNo order; empty when the adventure has none for it. */
export function chainFor(
  graph: StepperGraph | null,
  missionId: number | string,
) {
  if (!graph) return [];
  const id = String(missionId);
  return Object.values(graph.steps)
    .filter(
      (s) => s.stepType === "WebSmMissionStep" && s.options.MissionID === id,
    )
    .sort((a, b) => Number(a.options.StepNo) - Number(b.options.StepNo))
    .map((s) => ({
      stepName: s.stepName,
      text: s.options.Text ?? "",
      hint: s.options.Hint ?? "",
    }));
}

/** The step texts the screens use: the chain when the adventure has one,
 *  else the bank row's. */
export const stepTextsFor = (graph: StepperGraph | null, mission: Mission) => {
  // a handler's mission (after the promotion) is run from the webchat: no steps
  if (!(mission.missionId > 0)) return [];
  const chain = chainFor(graph, mission.missionId);
  return chain.length ? chain.map((s) => s.text) : stepsFor(mission);
};
